/**
 * Unified badminton game scoring rules.
 * Supports 15- and 21-point targets with standard (deuce) or no-deuce modes.
 */

/** @typedef {{ pointsTarget: 15|21, deuceMode: 'standard'|'no_deuce' }} ScoringConfig */

export const POINTS_TARGETS = [15, 21];
export const DEUCE_MODES = ['standard', 'no_deuce'];

export const DEFAULT_SCORING = Object.freeze({
  pointsTarget: 21,
  deuceMode: 'standard',
});

const STANDARD_RULES = {
  21: {
    target: 21,
    straightWinLoserMax: 19,
    deuceMinLoser: 20,
    deuceMaxWinner: 29,
    capWinner: 30,
    capLoserMin: 20,
  },
  15: {
    target: 15,
    straightWinLoserMax: 13,
    deuceMinLoser: 14,
    deuceMaxWinner: 19,
    capWinner: 21,
    capLoserMin: 20,
  },
};

/**
 * Normalize scoring config from session, tournament, or partial object.
 * Tournaments use `score_validation` for deuce mode; sessions use `deuce_mode`.
 * @param {object|null|undefined} raw
 * @returns {ScoringConfig}
 */
export function normalizeScoringConfig(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_SCORING };
  }

  const pointsTarget = raw.points_target ?? raw.pointsTarget;
  const deuceMode = raw.deuce_mode ?? raw.deuceMode ?? raw.score_validation;

  return {
    pointsTarget: pointsTarget === 15 ? 15 : 21,
    deuceMode: deuceMode === 'no_deuce' ? 'no_deuce' : 'standard',
  };
}

/**
 * @param {ScoringConfig} config
 * @returns {number}
 */
export function getMaxScore(config = DEFAULT_SCORING) {
  const { pointsTarget, deuceMode } = normalizeScoringConfig(config);
  if (deuceMode === 'no_deuce') return pointsTarget;
  return STANDARD_RULES[pointsTarget].capWinner;
}

/**
 * @param {ScoringConfig} config
 * @returns {number}
 */
export function getWalkoverPointsFor(config = DEFAULT_SCORING) {
  return normalizeScoringConfig(config).pointsTarget;
}

/**
 * @param {ScoringConfig} config
 * @returns {string}
 */
export function getScoringRulesSummary(config = DEFAULT_SCORING) {
  const { pointsTarget, deuceMode } = normalizeScoringConfig(config);

  if (deuceMode === 'no_deuce') {
    const loserMax = pointsTarget - 1;
    return `First to ${pointsTarget}, max ${pointsTarget} (${pointsTarget}–${loserMax} wins).`;
  }

  if (pointsTarget === 21) {
    return 'First to 21, max 30, win by 2 (except 30–29).';
  }

  return 'First to 15, max 21, win by 2 from 14–all (21–20 wins).';
}

/**
 * Short label for score-entry UI (court panel, forms).
 * @param {ScoringConfig|object} [config]
 * @returns {string}
 */
export function getScoringContextLabel(config = DEFAULT_SCORING) {
  const { pointsTarget, deuceMode } = normalizeScoringConfig(config);
  const deuceLabel = deuceMode === 'no_deuce' ? 'No deuce' : 'Standard deuce';
  return `Playing to ${pointsTarget} · ${deuceLabel}`;
}

/**
 * Validate a game score.
 * @param {number|string} score1
 * @param {number|string} score2
 * @param {ScoringConfig|object} [config]
 * @returns {string|null} Error message or null if valid
 */
export function validateGameScore(score1, score2, config = DEFAULT_SCORING) {
  const s1 = parseInt(score1, 10);
  const s2 = parseInt(score2, 10);

  if (isNaN(s1) || isNaN(s2)) return null;

  if (s1 < 0 || s2 < 0) return 'Scores cannot be negative';
  if (s1 === s2) return 'Scores cannot be equal';

  const { pointsTarget, deuceMode } = normalizeScoringConfig(config);

  if (deuceMode === 'no_deuce') {
    return validateNoDeuceScore(s1, s2, pointsTarget);
  }

  return validateStandardScore(s1, s2, pointsTarget);
}

/**
 * @param {number} s1
 * @param {number} s2
 * @param {15|21} target
 * @returns {string|null}
 */
function validateNoDeuceScore(s1, s2, target) {
  const winner = Math.max(s1, s2);
  const loser = Math.min(s1, s2);

  if (winner > target) return `Maximum score is ${target} (no deuce mode)`;
  if (winner < target) return `At least one team must reach ${target} points to win`;
  if (loser > target - 1) return `In no deuce mode, the losing score cannot exceed ${target - 1}`;
  return null;
}

/**
 * @param {number} s1
 * @param {number} s2
 * @param {15|21} target
 * @returns {string|null}
 */
function validateStandardScore(s1, s2, target) {
  const rules = STANDARD_RULES[target];
  const winner = Math.max(s1, s2);
  const loser = Math.min(s1, s2);

  if (winner < rules.target) {
    return `A team must score at least ${rules.target} points to win`;
  }

  if (winner > rules.capWinner) {
    return `Maximum score is ${rules.capWinner} points`;
  }

  if (winner === rules.target) {
    if (loser > rules.straightWinLoserMax) {
      return `If a team wins with ${rules.target}, the other team must have ${rules.straightWinLoserMax} or less`;
    }
    return null;
  }

  if (winner > rules.target && winner < rules.capWinner) {
    if (loser < rules.deuceMinLoser) {
      return `If a team wins with more than ${rules.target}, the other team must have at least ${rules.deuceMinLoser}`;
    }
    if (winner - loser !== 2) {
      return `Games above ${rules.target} must be won by exactly 2 points (e.g., ${rules.target + 1}-${rules.deuceMinLoser}, ..., ${rules.deuceMaxWinner}-${rules.deuceMaxWinner - 2})`;
    }
    return null;
  }

  if (winner === rules.capWinner) {
    if (loser < rules.capLoserMin) {
      return `If a team wins with ${rules.capWinner}, the other team must have at least ${rules.capLoserMin}`;
    }
    return null;
  }

  return 'Invalid score for badminton rules';
}

/**
 * @param {string|number} team1Score
 * @param {string|number} team2Score
 * @param {15|21} [pointsTarget=21]
 * @returns {boolean}
 */
export function shouldShowWinQuickButton(team1Score, team2Score, pointsTarget = 21) {
  const target = String(pointsTarget);
  return String(team1Score ?? '') !== target && String(team2Score ?? '') !== target;
}

/**
 * @param {'team1'|'team2'} team
 * @param {15|21} pointsTarget
 * @param {object} options
 * @param {function(string): void} options.setTeam1Score
 * @param {function(string): void} options.setTeam2Score
 * @param {{ current?: HTMLInputElement | null }} options.team1InputRef
 * @param {{ current?: HTMLInputElement | null }} options.team2InputRef
 * @param {function(): void} [options.onClearErrors]
 */
export function applyWinQuickScore(team, pointsTarget, options) {
  const {
    setTeam1Score,
    setTeam2Score,
    team1InputRef,
    team2InputRef,
    onClearErrors,
  } = options;
  const targetStr = String(pointsTarget);
  onClearErrors?.();
  if (team === 'team1') {
    setTeam1Score(targetStr);
    setTimeout(() => team2InputRef?.current?.focus?.(), 0);
  } else {
    setTeam2Score(targetStr);
    setTimeout(() => team1InputRef?.current?.focus?.(), 0);
  }
}
